import { IChapter, IControlledFields } from '../model/types'
import { ABIsType, ContracNames, ContractsStore } from '@/shared/model/store/contracts-store'
import { v4 as uuidv4 } from 'uuid'
import { formSchema, systemChapter } from '../model/model'
import { hexToBytes, keccak256 } from 'viem'
import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'
import { ContentLocales } from '@/entities/feed-block/model/types'
import { getGateWay } from '@/widgets/post/lib/utils'

export const compareValues = (state: ContentLocales, form: UseFormReturn<z.infer<typeof formSchema>>) => {
	const { locales } = form.getValues()

	for (const locale of Object.values(locales)) {
		for (const localeState of Object.values(state)) {
			if (localeState.title !== locale.title) return true

			if (localeState.data !== locale.data) return true
		}
	}

	return false
}

export const currentTotalSize = (files: File[]) => files.reduce((acc, file) => acc + file.size, 0)

export const getSubstringFromLastDot = input => {
	const lastDotIndex = input.lastIndexOf('.')
	return lastDotIndex !== -1 ? input.substring(lastDotIndex) : ''
}

export async function getContentFromHash(hash: string) {
	if (!hash) return ''
	const contentData = await fetch(`${getGateWay()}${hash}`)
	return contentData.text()
}

export const getAbis = async data => {
	const state = ContractsStore.getState()
	const contracts = data.savva_contracts
	const ABI: ABIsType = {} as ABIsType
	const oldABI: ABIsType = state.contracts

	for await ( const property of Object.keys(contracts)) {
		const prop: ContracNames = property as ContracNames
		const needLoadAbi = !oldABI[prop]?.abi || oldABI[prop]?.abi_cid !== contracts[property]?.abi_cid

		ABI[property] = {
			abi_cid: contracts[property].abi_cid,
			address: contracts[property].address,
			abi:
				needLoadAbi
					? JSON.parse(await getContentFromHash(contracts[property].abi_cid))
					: oldABI[property].abi
		}
	}
	ContractsStore.setState({ contracts: ABI })
}

export const createCid = (address: `0x${string}`, domain: string, guid?: string) => {
	const uid = guid || uuidv4()

	const packed = [...hexToBytes(address), ...Buffer.from(domain), ...Buffer.from(uid)]
	const hash = keccak256(new Uint8Array(packed))

	return { uid, hash: `${hash}` }
}

export const isComment = (value: string) => ['comment', 'comment-edit'].includes(value)


export const getNameData = (controlledFields: IControlledFields[], currentChapter: string) => {
	const idx = controlledFields?.findIndex((el, idx) => el.id === currentChapter && idx !== 0)
	return idx > 0 ? `chapters.${idx}.data` : `data`
}

export const createChapters = chapters => {
	const newChapter = {
		title: '',
		data: '',
		id: Math.random().toString()
	}
	return !chapters.length ? [systemChapter, newChapter] : [...chapters, newChapter]
}

export const removeStrFromData = (data: string, name: string) => {
	// Разбиваем вручную текст на массив "строк" по определению блоков ([ и ])
	const result = []
	let current = ''
	let insideBrackets = false

	for (const char of data) {
		if (char === '[') {
			insideBrackets = true // Начало ссылки
		}
		if (char === ']' && insideBrackets) {
			current += char // Закрываем блок
			insideBrackets = false
			continue
		}
		current += char

		if (!insideBrackets && char === ')') {
			// Конец блока URL
			result.push(current)
			current = ''
		}
	}

	// Если остался незакрытый блок, добавляем
	if (current.trim()) {
		result.push(current)
	}

	// Удаляем строки с нужным именем файла
	const filteredResult = result.filter(line => !line.includes(name))

	// Объединяем оставшиеся строки обратно
	const updatedData = filteredResult.join('').trim()

	return updatedData
}

export const removeStrFromChapters = (chapters: IChapter[], name: string) => {
	const updateChapters = chapters.map(obj => {
		// Если объект содержит поле 'data' и оно включает имя файла
		if (obj.data && obj.data.includes(name)) {
			// Разбиваем строку 'data' на части и фильтруем
			const result = []
			let current = ''
			let insideBrackets = false

			for (const char of obj.data) {
				if (char === '[') {
					insideBrackets = true // Начало ссылки
				}
				if (char === ']' && insideBrackets) {
					current += char // Закрытие блока
					insideBrackets = false
					continue
				}
				current += char

				if (!insideBrackets && char === ')') {
					// Конец блока URL
					result.push(current)
					current = ''
				}
			}

			// Если остался незакрытый блок, добавляем
			if (current.trim()) {
				result.push(current)
			}

			// Фильтруем строки, содержащие имя файла
			const filteredResult = result.filter(line => !line.includes(name))

			// Обновляем поле 'data' в объекте
			obj.data = filteredResult.join('').trim()
		}

		return obj
	})

	return updateChapters
}

interface Chapter {
    title: string;
    data: string;
    id: string;
}

interface LocaleData {
    title: string;
    data: string;
    tags: string[];
    chapters: Chapter[];
    categories: string[];
    currentChapter?: string;
}

interface PostData {
    files: any[];
    thumbnail: string;
    locales: {
        [key: string]: LocaleData;
    };
    isRegisterNewPost: boolean;
    isNsfw: boolean;
    isComment: boolean;
}


export function validatePostData(postData: PostData, isComment?: boolean ): { isValid: boolean; error?: string } {
    const { locales } = postData;

    // Проверка, есть ли вообще локали
    if (!locales || Object.keys(locales).length === 0) {
        return { isValid: false, error: "No locales found." };
    }

    // Проверяем каждую локаль
    const hasValidLocale = Object.values(locales).some((locale) => {
        const hasTitle = isComment ? true : Boolean(locale.title?.trim());
        const hasData = Boolean(locale.data?.trim());

        // Проверяем, есть ли хотя бы одна валидная глава
        const hasValidChapters = locale.chapters?.some(
            (chapter) => isComment ? true : Boolean(chapter.title?.trim()) && Boolean(chapter.data?.trim())
        );

        // Условие: (title + data) ИЛИ (title + хотя бы одна валидная глава)
        return (hasTitle && hasData) || (hasTitle && hasValidChapters);
    });

    if (!hasValidLocale) {
        return {
            isValid: false,
            error: "At least one locale must have either:\n- title + data, OR\n- title + at least one chapter with title and data.",
        };
    }

    return { isValid: true };
}
